import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { safeGetByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import {
  RelatedTasksRequest,
  useRelatedTasks,
} from "@coworker/apprestructured/src/task-management/services/useRelatedTasks";
import { useTranslation } from "@coworker/locales";
import { VisibilityCondition } from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import styles from "./RelatedTasksComponent.module.css";
import { RelatedTasksModal } from "./RelatedTasksModal";
import { useState } from "react";
import { CreatorFragmentCommonProps } from "../TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";

export type RelatedTasksConfigProps = {
  visibility?: VisibilityCondition;
};

export const RELATED_TASKS_ARTICLE_PATH = "details.item.no";

export function getArticleFieldFromVisibility(
  visibility: VisibilityCondition | undefined
): string | null {
  if (!visibility) return null;

  if ("conditions" in visibility) {
    for (const condition of visibility.conditions) {
      const field = getArticleFieldFromVisibility(condition);
      if (field) return field;
    }
    return null;
  }

  return visibility.field === RELATED_TASKS_ARTICLE_PATH
    ? visibility.field
    : null;
}

const issueLabelByValue: Record<string, string> = {
  inventoryError: "issueNameInventoryString",
  damagedOrBroken: "issueNameDamagedString",
  poorlyAssembled: "issueNameAssemblyString",
  dirty: "issueNameDirtyString",
  missingParts: "issueNameMissingPartsString",
  noPTag: "issueNameNoPriceTagString",
  racking: "issueNameRackingString",
  wrongSalesLocation: "issueNameWrongSalesLocationString",
  notInStock: "issueNameOutOfStockString",
  missingDisplayModel: "issueNameMissingDisplayModelString",
  other: "issueNameOtherString",
};

type RelatedTasksComponentProps = {
  task: CreatorFragmentCommonProps["task"];
  taskTypeId: string;
  fields: RelatedTasksConfigProps;
};

function getRequest(
  task: RelatedTasksComponentProps["task"],
  taskTypeId: string,
  locale: string,
  fields: RelatedTasksConfigProps
) {
  if (isLoading(task)) return null;
  const articleField =
    getArticleFieldFromVisibility(fields.visibility) ||
    RELATED_TASKS_ARTICLE_PATH;

  const articleNo = safeGetByPath(task, articleField);
  if (typeof articleNo !== "string" || !articleNo.trim()) return null;

  return {
    taskTypeId,
    articleNo: articleNo.trim(),
    locale,
  } as RelatedTasksRequest;
}

export function RelatedTasksComponent({
  task,
  taskTypeId,
  fields,
}: RelatedTasksComponentProps) {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const locale =
    i18n?.resolvedLanguage ||
    i18n?.language ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("hej-language")) ||
    (typeof document !== "undefined" && document.documentElement.lang) ||
    "en";
  const normalizedLocale = locale.replace(/_/g, "-");
  const request = getRequest(task, taskTypeId, normalizedLocale, fields);
  const { data, isLoading, isError } = useRelatedTasks(request);
  const matches = data?.tasks ?? [];
  const summaryFromRelatedTasks = data?.summary ?? null;
  if (!request) return null;
  if (isLoading)
    return (
      <FixaText className={styles["loading"]}>{t("loadingString")}</FixaText>
    );
  if (isError)
    return (
      <FixaInlineMessage
        variant="negative"
        title={t("somethingWentWrongString", {
          defaultValue: "Unable to check for related tasks.",
        })}
      />
    );
  if (matches.length === 0) return null;
  const totalTasks = summaryFromRelatedTasks?.totalTasks ?? matches.length;
  const deterministicSummary =
    summaryFromRelatedTasks?.issueGroups
      ?.slice()
      .sort((a, b) => b.totalCount - a.totalCount)
      .map((group) => {
        const issueKey = issueLabelByValue[group.issueValue];
        const issueLabel = issueKey
          ? t(issueKey, { defaultValue: group.issueValue })
          : group.issueValue;
        const locationCount = group.locations.length;
        const locations = group.locations
          .map(({ location }) => location)
          .join(", ");
        const issueSummaryKey =
          locationCount === 1
            ? "relatedTasksIssueSummarySingleLocationString"
            : "relatedTasksIssueSummaryMultipleLocationsString";

        return t(issueSummaryKey, {
          count: group.totalCount,
          issue: issueLabel,
          locations,
          defaultValue:
            locationCount === 1
              ? group.totalCount === 1
                ? "{{count}} {{issue}} task found in location {{locations}}."
                : "{{count}} {{issue}} tasks found in location {{locations}}."
              : group.totalCount === 1
                ? "{{count}} {{issue}} task found in locations {{locations}}."
                : "{{count}} {{issue}} tasks found in locations {{locations}}.",
        });
      })
      .join(" ") ?? "";
  const summaryText =
    summaryFromRelatedTasks?.llmSummary ||
    deterministicSummary ||
    summaryFromRelatedTasks?.summaryText ||
    t("relatedTasksFallbackSummaryString", {
      count: totalTasks,
      defaultValue:
        totalTasks === 1
          ? "Found {{count}} related task for this article."
          : "Found {{count}} related tasks for this article.",
    });
  const summaryTitle = `${t("summaryString", {
    defaultValue: "Summary",
  })} ${t("ofString", {
    defaultValue: "of",
  })} ${t("relatedTasksCountString", {
    count: totalTasks,
    defaultValue:
      totalTasks === 1 ? "{{count}} related task" : "{{count}} related tasks",
  })}`;

  const viewAllAction = {
    text: t("viewString", {
      defaultValue: "View tasks",
    }),
    onClick: () => setIsModalOpen(true),
  };

  return (
    <FixaListViewItem
      inset
      description={
        <>
          <FixaInlineMessage
            variant="informative"
            title={summaryTitle}
            body={summaryText}
            actions={[viewAllAction]}
          />

          <RelatedTasksModal
            matches={matches}
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        </>
      }
    />
  );
}
